import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Loader2, Mail, Lock, User, Gift, Check, ChevronDown, ChevronUp } from 'lucide-react';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi } = useApi();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValid, setCodeValid] = useState<boolean | null>(null);

  // Check for referral code from URL or session storage
  useEffect(() => {
    const urlCode = searchParams.get('ref');
    const storedCode = sessionStorage.getItem('referralCode');
    const code = urlCode || storedCode;

    if (code) {
      setReferralCode(code.toUpperCase());
      setShowReferralInput(true);
      validateReferralCode(code);

      // Store in session for persistence across page loads
      if (urlCode) {
        sessionStorage.setItem('referralCode', urlCode.toUpperCase());
      }
    }
  }, [searchParams]);

  async function validateReferralCode(code: string) {
    if (!code || code.length !== 7) {
      setCodeValid(null);
      setReferrerName(null);
      return;
    }

    setValidatingCode(true);
    try {
      const data = await fetchApi<{ valid: boolean; referrerName?: string }>('/api/referrals/validate', {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      setCodeValid(data.valid);
      setReferrerName(data.valid ? data.referrerName || null : null);
    } catch {
      setCodeValid(false);
      setReferrerName(null);
    } finally {
      setValidatingCode(false);
    }
  }

  function handleReferralCodeChange(value: string) {
    const upperValue = value.toUpperCase().slice(0, 7);
    setReferralCode(upperValue);
    setCodeValid(null);
    setReferrerName(null);

    // Validate when code is 7 characters
    if (upperValue.length === 7) {
      validateReferralCode(upperValue);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      // Pass referral code if valid
      const validCode = codeValid && referralCode.length === 7 ? referralCode : undefined;
      await register(email, password, name || undefined, validCode);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto mt-12">
      <div className="bg-background-secondary rounded-xl p-8 border border-gray-800">
        <h1 className="text-2xl font-bold text-white mb-2">Create your account</h1>
        <p className="text-gray-400 mb-6">Start your AI learning journey today</p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
              Name (optional)
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue"
                placeholder="Your name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue"
                placeholder="At least 8 characters"
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1.5">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          {/* Referral Code Section */}
          <div className="pt-2">
            {!showReferralInput ? (
              <button
                type="button"
                onClick={() => setShowReferralInput(true)}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
              >
                <Gift className="w-4 h-4" />
                Have a referral code?
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReferralInput(false);
                    setReferralCode('');
                    setCodeValid(null);
                    setReferrerName(null);
                  }}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
                >
                  <Gift className="w-4 h-4" />
                  Referral code
                  <ChevronUp className="w-4 h-4" />
                </button>
                <div className="relative">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => handleReferralCodeChange(e.target.value)}
                    placeholder="Enter 7-character code"
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg text-white placeholder-gray-500 font-mono tracking-wider uppercase focus:ring-1 transition-colors ${
                      codeValid === true
                        ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                        : codeValid === false
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-700 focus:border-accent-blue focus:ring-accent-blue'
                    }`}
                    maxLength={7}
                  />
                  {validatingCode && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                  {!validatingCode && codeValid === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                </div>
                {codeValid === true && referrerName && (
                  <p className="text-sm text-green-400 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Invited by {referrerName}
                  </p>
                )}
                {codeValid === false && (
                  <p className="text-sm text-red-400">
                    Invalid referral code
                  </p>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-accent-blue text-slate-900 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-accent-blue hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
